import React from 'react';
import { Navbar } from '../layouts/Navbar';
import { Footer } from '../layouts/Footer';

export const Products = () => {
    // Footer header content for products page
    const footerHeader = ["Transform Your Healthcare Practice", "Discover our innovative solutions"];

    const products = [
        {
            id: 1,
            name: "Clinqo Queue Management",
            description: "Revolutionary queue management system that reduces waiting times and improves patient satisfaction.",
            features: [
                "Real-time queue tracking",
                "WhatsApp notifications",
                "Patient check-in system",
                "Analytics dashboard",
                "Multi-location support"
            ],
            price: "Starting at ₹2,999/month",
            category: "Core Platform"
        },
        {
            id: 2,
            name: "Clinqo WhatsApp Integration",
            description: "Seamless WhatsApp communication for appointment reminders and queue updates.",
            features: [
                "Automated appointment reminders",
                "Queue position updates",
                "Two-way communication",
                "Template management",
                "Delivery tracking"
            ],
            price: "₹999/month",
            category: "Communication"
        },
        {
            id: 3,
            name: "Clinqo Analytics Pro",
            description: "Advanced analytics and reporting to optimize your clinic operations.",
            features: [
                "Patient flow analytics",
                "Performance metrics",
                "Custom reports",
                "Predictive insights",
                "ROI tracking"
            ],
            price: "₹1,499/month",
            category: "Analytics"
        },
        {
            id: 4,
            name: "Clinqo Mobile App",
            description: "Native mobile application for patients to manage their appointments and queue status.",
            features: [
                "Appointment booking",
                "Real-time queue status",
                "Push notifications",
                "Clinic finder",
                "Medical history access"
            ],
            price: "Included with Core Platform",
            category: "Mobile Solution"
        }
    ];

    const benefits = [
        {
            icon: "⏰",
            title: "Reduce Waiting Time",
            description: "Cut patient waiting times by up to 60% with intelligent queue management."
        },
        {
            icon: "📱",
            title: "WhatsApp Integration",
            description: "Seamless communication through India's most popular messaging platform."
        },
        {
            icon: "📊",
            title: "Real-time Analytics",
            description: "Make data-driven decisions with comprehensive reporting and insights."
        },
        {
            icon: "🔧",
            title: "Easy Integration",
            description: "Quick setup with existing clinic management systems and workflows."
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <br />
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Products</h1>
                        <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                            Comprehensive healthcare technology solutions designed to streamline your clinic operations and enhance patient experience.
                        </p>
                        <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Schedule a Demo
                        </button>
                    </div>
                </div>
            </div>

            {/* Benefits Section */}
            <div className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Clinqo?</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Our solutions are built specifically for healthcare providers in India, understanding local needs and challenges.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition duration-300">
                                <div className="text-4xl mb-4">{benefit.icon}</div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                                <p className="text-gray-600">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Products Section */}
            <div className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Product Suite</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Each product is designed to work independently or as part of a complete solution.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {products.map((product) => (
                            <div key={product.id} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition duration-300">
                                <div className="mb-4">
                                    <span className="inline-block bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full font-medium">
                                        {product.category}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h3>
                                <p className="text-gray-600 mb-6">{product.description}</p>
                                
                                <div className="mb-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Key Features:</h4>
                                    <ul className="space-y-2">
                                        {product.features.map((feature, index) => (
                                            <li key={index} className="flex items-center text-gray-600">
                                                <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className="text-2xl font-bold text-blue-600">{product.price}</span>
                                    </div>
                                    <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition duration-300">
                                        Learn More
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-blue-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
                    <p className="text-xl mb-8 max-w-2xl mx-auto">
                        Join hundreds of healthcare providers who have transformed their practice with Clinqo.
                    </p>
                    <div className="space-x-4">
                        <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Start Free Trial
                        </button>
                        <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition duration-300">
                            Contact Sales
                        </button>
                    </div>
                </div>
            </div>

            <Footer header={footerHeader} />
        </div>
    );
};
